import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/UseToast';
import { PATH } from 'src/features/navigation/utils/constants';
import { useNavigate } from 'react-router-dom';
import { useParking } from 'src/providers/ParkingProvider';
import { GET_MEMBERSHIP_RESOURCE, useGetMembership } from 'src/features/membership/api/useGetMembership';
import { useConvertMembership } from 'src/features/membership/api/useConvertMembership';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { convertMembershipFormSchema, ConvertMembershipFormSchemaValues } from 'src/features/membership/formSchema';
import ConvertMembershipForm from 'src/features/membership/components/forms/ConvertMembershipForm';
import { Membership } from 'src/features/membership/types';

interface ConvertMembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership?: Membership;
    children?: React.ReactNode;
    isOpen?: boolean;
}

function ConvertMembershipFormModal<TData>({ selectedMembership, children }: ConvertMembershipFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { parkingId } = useParking();

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: membership } = useGetMembership({
        membershipId: selectedMembership?.id ?? '',
        parkingId,
        config: { enabled: !!selectedMembership?.id && isModalOpen },
    });

    const onError = useOnError();

    const { mutate: convertMembership, isPending } = useConvertMembership({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [GET_MEMBERSHIP_RESOURCE] });
            setIsModalOpen(false);

            void navigate(`/${PATH.MEMBERSHIPS}/all/${success.id}`);

            toast({
                title: formatMessage(membershipMessages.convertMembershipSuccess),
                description: formatMessage(membershipMessages.convertMembershipSuccessDescription, {
                    entry: success.name,
                }),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    const defaultValues = React.useMemo(() => {
        return {
            membership_type: undefined,
            max_allowed_spaces_count: undefined,
            valid_from: undefined,
            valid_to: undefined,
        };
    }, []);

    const form = useForm<ConvertMembershipFormSchemaValues>({
        resolver: zodResolver(convertMembershipFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [membership, isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ConvertMembershipFormSchemaValues) => {
            convertMembership({
                parkingId,
                membershipId: selectedMembership?.id ?? '',
                ...values,
            });
        },
        [convertMembership, parkingId, selectedMembership?.id],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger>
                    <Button className={cn('ml-auto')}>
                        <FontAwesomeIcon icon={ICON_MAP.CONVERT} />
                        {formatMessage(membershipMessages.convertMembership)}
                    </Button>
                </DialogTrigger>
            )}

            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    <SquareIcon icon={ICON_MAP.CONVERT} />
                    <div>
                        <DialogTitle>{formatMessage(membershipMessages.convertMembership)}</DialogTitle>
                        <DialogDescription>
                            {formatMessage(membershipMessages.convertMembershipSubtitle, {
                                entry: selectedMembership?.name,
                            })}
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ConvertMembershipForm form={form} onSubmit={onSubmit} selectedMembership={selectedMembership} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(membershipMessages.convertMembership)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ConvertMembershipFormModal;
